import { useState } from 'react';
import type { JSX } from 'react';
import { Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@artosera/ui';
import type { EtatParc } from '@artosera/shared';
import type { VueDAigle } from '../../types.js';
import commun from './commun.module.css';
import { Section } from './composants.js';
import { useFormats } from './formats.js';
import { BarresEmpilees, HistoriqueCourt, type SerieBarres } from './graphiques.js';
import { ListeBarres, type LigneBarre } from './ListeBarres.js';
import styles from './Parc.module.css';

/** Les galeries montrées d'abord dans le détail ; le reste se déplie. */
const GALERIES_VISIBLES = 8;

const PASTILLE_ETAT: Record<EtatParc, string> = {
  production: commun.pastilleVert!,
  essai: commun.pastilleAmbre!,
  sans_contrat: commun.pastilleAccent!,
  suspendue: commun.pastilleGris!,
  resiliee: commun.pastilleGris!,
};

// Les états du filtre : ceux du `Record` ci-dessus, dans son ordre (exhaustif par le typage).
const ETATS = Object.keys(PASTILLE_ETAT) as EtatParc[];

/**
 * Le parc : des compteurs anonymes pour tout le monde (état, palier, tranche
 * d'usage), et — vue admin seulement — le détail nommé, galerie par galerie.
 * L'API ne met `parc.detail` que dans la vue admin ; sans lui, rien n'est rendu.
 */
export function Parc({ vue, numero }: { vue: VueDAigle; numero: string }): JSX.Element {
  const { t } = useTranslation('console');
  const f = useFormats();
  const { parc } = vue;
  const historique = parc.historique;

  const colonnes = historique.map((p) => ({
    court: f.moisCourt(p.mois),
    long: f.moisLong(p.mois),
  }));
  const toutesSeries: SerieBarres[] = [
    {
      cle: 'production',
      libelle: t('vueDAigle.parc.serie.production'),
      couleur: 'production',
      valeurs: historique.map((p) => p.production),
    },
    {
      cle: 'essai',
      libelle: t('vueDAigle.parc.serie.essai'),
      couleur: 'essai',
      valeurs: historique.map((p) => p.essai),
    },
    {
      cle: 'sansContrat',
      libelle: t('vueDAigle.parc.serie.sansContrat'),
      couleur: 'sansContrat',
      valeurs: historique.map((p) => p.sansContrat),
    },
    {
      cle: 'resiliees',
      libelle: t('vueDAigle.parc.serie.resiliees'),
      couleur: 'resiliees',
      valeurs: historique.map((p) => p.resiliees),
    },
  ];
  // « Sans contrat » n'a sa place dans le graphique que s'il y a eu des cas.
  const series = toutesSeries.filter(
    (s) => s.cle !== 'sansContrat' || s.valeurs.some((v) => v > 0),
  );

  // Compteurs : la part de chaque ligne dans son propre ensemble.
  const parEtat = parc.parEtat;
  const totalEtats =
    parEtat.production +
    parEtat.essai +
    parEtat.sansContrat +
    parEtat.suspendues +
    parEtat.resiliees;
  const lignesEtat: LigneBarre[] = (
    [
      ['production', parEtat.production, 'vert', true],
      ['essai', parEtat.essai, 'ambre', true],
      ['sansContrat', parEtat.sansContrat, undefined, false],
      ['suspendues', parEtat.suspendues, 'gris', false],
      ['resiliees', parEtat.resiliees, 'gris', false],
    ] as const
  )
    .filter(([, nombre, , toujours]) => toujours || nombre > 0)
    .map(([cle, nombre, couleur]) => ({
      cle,
      libelle: t(`vueDAigle.parc.serie.${cle}`),
      score: f.nombre(nombre),
      detail: totalEtats > 0 ? f.pourcent(nombre / totalEtats) : undefined,
      ratio: totalEtats > 0 ? nombre / totalEtats : 0,
      couleur,
    }));

  const totalPaliers = parc.parPalier.reduce((total, ligne) => total + ligne.nombre, 0);
  const lignesPalier: LigneBarre[] = parc.parPalier.map((ligne) => ({
    cle: ligne.palier,
    libelle: t('vueDAigle.parc.palier', {
      palier: t(`galeries.palier.${ligne.palier}`, { defaultValue: ligne.palier }),
    }),
    score: f.nombre(ligne.nombre),
    detail: totalPaliers > 0 ? f.pourcent(ligne.nombre / totalPaliers) : undefined,
    ratio: totalPaliers > 0 ? ligne.nombre / totalPaliers : 0,
  }));

  const totalTranches = parc.parTrancheDeModules.reduce((total, ligne) => total + ligne.nombre, 0);
  const lignesTranche: LigneBarre[] = parc.parTrancheDeModules.map((ligne) => ({
    cle: ligne.tranche,
    libelle: t(`vueDAigle.parc.tranche.${ligne.tranche}`),
    score: f.nombre(ligne.nombre),
    detail: totalTranches > 0 ? f.pourcent(ligne.nombre / totalTranches) : undefined,
    ratio: totalTranches > 0 ? ligne.nombre / totalTranches : 0,
    couleur: 'encre',
  }));

  return (
    <Section
      id="parc"
      numero={numero}
      titre={t('vueDAigle.parc.titre')}
      note={t('vueDAigle.parc.note')}
    >
      <div className={`${commun.deuxColonnes} ${styles.grille}`}>
        <div>
          {historique.length >= 2 ? (
            <BarresEmpilees
              colonnes={colonnes}
              series={series}
              legendeTableau={t('vueDAigle.parc.graphique')}
              enteteLibelle={t('vueDAigle.parc.colMois')}
              enteteTotal={t('vueDAigle.parc.colTotal')}
              formatNombre={f.nombre}
            />
          ) : (
            <HistoriqueCourt />
          )}
        </div>
        <div>
          <p className={commun.sousTitre}>{t('vueDAigle.parc.compteurs')}</p>
          <div className={styles.compteurs}>
            <section aria-labelledby="parc-etat">
              <h3 className={styles.sousTitreFin} id="parc-etat">
                {t('vueDAigle.parc.parEtat', { count: totalEtats })}
              </h3>
              <ListeBarres lignes={lignesEtat} />
            </section>
            {lignesPalier.length > 0 ? (
              <section aria-labelledby="parc-palier">
                <h3 className={styles.sousTitreFin} id="parc-palier">
                  {t('vueDAigle.parc.parPalier')}
                </h3>
                <ListeBarres lignes={lignesPalier} />
              </section>
            ) : null}
            {lignesTranche.length > 0 ? (
              <section aria-labelledby="parc-tranche">
                <h3 className={styles.sousTitreFin} id="parc-tranche">
                  {t('vueDAigle.parc.parTranche')}
                </h3>
                <ListeBarres lignes={lignesTranche} />
              </section>
            ) : null}
          </div>
          <p className={commun.noteDiscrete}>{t('vueDAigle.parc.noteAnonyme')}</p>
        </div>
      </div>

      {parc.detail ? <Detail galeries={parc.detail} /> : null}
    </Section>
  );
}

/** Le détail par galerie : vue admin seulement. Nom, état, palier, modules, date. */
function Detail({ galeries }: { galeries: NonNullable<VueDAigle['parc']['detail']> }): JSX.Element {
  const { t } = useTranslation('console');
  const f = useFormats();
  const [filtre, setFiltre] = useState<'tous' | EtatParc>('tous');
  const [toutVoir, setToutVoir] = useState(false);

  const selection = filtre === 'tous' ? galeries : galeries.filter((g) => g.etat === filtre);
  const visibles = toutVoir ? selection : selection.slice(0, GALERIES_VISIBLES);

  function choisir(nouveau: 'tous' | EtatParc): void {
    setFiltre(nouveau);
    setToutVoir(false);
  }

  return (
    <div className={styles.detail}>
      <p className={styles.noteAnonyme}>
        <Eye size={16} strokeWidth={1.5} aria-hidden="true" />
        <span>
          <strong>{t('vueDAigle.parc.noteAssocies')}</strong>{' '}
          {t('vueDAigle.parc.noteAssociesTexte')}
        </span>
      </p>
      <p className={commun.sousTitre}>{t('vueDAigle.parc.detail')}</p>
      <div className={styles.filtres} role="group" aria-label={t('vueDAigle.parc.filtre')}>
        <button type="button" aria-pressed={filtre === 'tous'} onClick={() => choisir('tous')}>
          {t('vueDAigle.parc.toutes', { count: galeries.length })}
        </button>
        {ETATS.map((etat) => {
          const nombre = galeries.filter((g) => g.etat === etat).length;
          if (nombre === 0) return null;
          return (
            <button
              key={etat}
              type="button"
              aria-pressed={filtre === etat}
              onClick={() => choisir(etat)}
            >
              {t('vueDAigle.parc.filtreEtat', {
                etat: t(`vueDAigle.parc.etat.${etat}`),
                count: nombre,
              })}
            </button>
          );
        })}
      </div>
      <div className={commun.tableauConteneur}>
        <table className={`${commun.table} ${commun.tableEmpile}`}>
          <thead>
            <tr>
              <th scope="col">{t('vueDAigle.parc.colGalerie')}</th>
              <th scope="col">{t('vueDAigle.parc.colEtat')}</th>
              <th scope="col">{t('vueDAigle.parc.colPalier')}</th>
              <th scope="col" className={commun.num}>
                {t('vueDAigle.parc.colModules')}
              </th>
              <th scope="col" className={commun.num}>
                {t('vueDAigle.parc.colDate')}
              </th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((galerie) => (
              <tr key={galerie.id}>
                <td
                  className={`${commun.premiere} ${commun.cellulePrincipale}`}
                  data-label={t('vueDAigle.parc.colGalerie')}
                >
                  <Link to={`/galeries/${galerie.id}`} className={styles.lienGalerie}>
                    {galerie.nom}
                  </Link>
                </td>
                <td data-label={t('vueDAigle.parc.colEtat')}>
                  <span className={`${commun.pastille} ${PASTILLE_ETAT[galerie.etat] ?? ''}`}>
                    {t(`vueDAigle.parc.etat.${galerie.etat}`)}
                  </span>
                </td>
                <td data-label={t('vueDAigle.parc.colPalier')}>
                  {t(`galeries.palier.${galerie.palier}`, { defaultValue: galerie.palier })}
                </td>
                <td className={commun.num} data-label={t('vueDAigle.parc.colModules')}>
                  {f.nombre(galerie.modules)}
                </td>
                <td className={commun.num} data-label={t('vueDAigle.parc.colDate')}>
                  {galerie.etat === 'production' && galerie.entreeLe
                    ? f.date(galerie.entreeLe)
                    : galerie.etat === 'essai' && galerie.finEssaiLe
                      ? t('vueDAigle.parc.finEssaiLe', { date: f.jourMois(galerie.finEssaiLe) })
                      : '—'}
                </td>
              </tr>
            ))}
            {visibles.length === 0 ? (
              <tr>
                <td colSpan={5}>{t('vueDAigle.parc.aucune')}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <div className={commun.tablePied}>
        <span>
          {t('vueDAigle.parc.affichage', { n: visibles.length, total: selection.length })}
        </span>
        {selection.length > GALERIES_VISIBLES ? (
          <Button variant="ghost" size="sm" onClick={() => setToutVoir((v) => !v)}>
            {toutVoir
              ? t('vueDAigle.parc.reduire')
              : t('vueDAigle.parc.afficherTout', { count: selection.length })}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
